// What the client is willing to believe a response contains.
//
// This module is the mirror image of `packages/api/src/views.ts`, and it is written the
// same way and for a stronger reason. That module decides what leaves the server. This one
// decides what EXISTS in the browser at all — and acceptance case 4 is not "we do not
// render context items today", it is "there is no code path that would render one if it
// appeared in a response".
//
// You cannot make that promise by being careful in components. A component renders what it
// is given, and `{...message}` spread into a `<pre>` during some future debugging session
// is one line. The promise is only structural if the extra key never becomes a value in
// this process. So:
//
//   * every decoder below names its fields ONE AT A TIME off a `Record<string, unknown>`;
//   * no decoder spreads, `Object.assign`es, or iterates the keys of what it was handed;
//   * the decoded object is frozen, and its type has no index signature, so a component
//     that wants `decision.revisions` does not compile and a component that reaches for it
//     dynamically finds `undefined`;
//   * `test/absence.test.ts` feeds every response a `contextItems`, a `telemetry`, a
//     `revisions` and a `reasoning` key full of sentinels, renders the whole application,
//     and asserts that no sentinel reaches the document. It also greps this package's
//     source for those names.
//
// The cost is that a field genuinely added to the API is invisible here until someone adds
// a line. That is the failure that is safe, and it is the same trade `views.ts` makes.
//
// ---------------------------------------------------------------------------
// `facilitatorBusy`
// ---------------------------------------------------------------------------
//
// `docs/archived/09-web-client.md` asks the plan read for a coarse "an activation is running for
// this plan", set server-side while a lease is held, because the client cannot infer it.
// It is in `VIEW_KEYS.plan` now, and it means: an activation lease exists and has not
// expired. An EXPIRED lease reads false — a dead worker is not a thinking facilitator, and
// a plan whose worker crashed must not look permanently thoughtful.
//
// It is read DEFENSIVELY all the same — absent, null, or not a boolean all mean false —
// because the failure that direction is a missing indicator and the failure the other
// direction is a family watching a facilitator that never stops considering. The server
// spent a rule on making a stale lease read false; a client that read a missing field as
// true would undo it from the other end.
//
// WHAT IT DOES NOT DO IS ARRIVE ON ITS OWN. The lease columns are excluded from the
// delivery layer's change fingerprint, so taking a lease signals nobody. See
// `plan/usePlan.ts` for what this client does about that and why.
//
// ---------------------------------------------------------------------------
// `self`
// ---------------------------------------------------------------------------
//
// The second field this contract grew, and the only one anywhere in this client that says
// who the reader is. It rides on the PERSON, not on a session, not on a `/me` call and not
// on the message: an account holds a different roster entry in every plan, so the answer is
// only true of the plan it arrived with, and holding it anywhere else would be holding a
// piece of identity that can disagree with the server about whose words these are.
//
// What it deliberately is not is an account id. The API has never named an account in a
// response and does not start now — `accountId` on the roster would tell everyone in the
// plan which entries are backed by which accounts, which is an identifier that spans
// plans, in order to answer a question about the reader. One bit, about yourself, is the
// whole of it, and `test/absence.test.tsx` greps this package to keep the other version out.

/** A JSON object, before anything has been believed about it. */
type Raw = Record<string, unknown>;

function object(value: unknown): Raw {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new MalformedResponse('expected an object');
  }
  return value as Raw;
}

function array(value: unknown): unknown[] {
  if (!Array.isArray(value)) {
    throw new MalformedResponse('expected an array');
  }
  return value;
}

function str(raw: Raw, key: string): string {
  const value = raw[key];
  if (typeof value !== 'string') {
    throw new MalformedResponse(`expected ${key} to be a string`);
  }
  return value;
}

function nullableStr(raw: Raw, key: string): string | null {
  const value = raw[key];
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== 'string') {
    throw new MalformedResponse(`expected ${key} to be a string or null`);
  }
  return value;
}

function nullableNum(raw: Raw, key: string): number | null {
  const value = raw[key];
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== 'number') {
    throw new MalformedResponse(`expected ${key} to be a number or null`);
  }
  return value;
}

function bool(raw: Raw, key: string): boolean {
  const value = raw[key];
  if (typeof value !== 'boolean') {
    throw new MalformedResponse(`expected ${key} to be a boolean`);
  }
  return value;
}

/** Absent, null, or not a boolean all mean the same thing: no. */
function optionalBool(raw: Raw, key: string): boolean {
  return raw[key] === true;
}

export class MalformedResponse extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MalformedResponse';
  }
}

// ------------------------------------------------------------------------- the shapes

/**
 * A plan, in five fields.
 *
 * `updatedAt` is deliberately ABSENT, and unlike `role` on a person the server does not send
 * it either — it was removed from `VIEW_KEYS.plan` at the same time as it was removed from
 * here. It was `plan.updated_at`, a column a database trigger moves on every write to the
 * plan row: the activation watermark, the stuck marker, every plan event, and the worker's
 * lease heartbeat, every thirty seconds for as long as the facilitator is working. It never
 * meant "when this plan last changed". Nothing in this client ever rendered it, which is the
 * only reason it was harmless — the day a screen drew "last updated" it would have drawn the
 * heartbeat, to a family, in a browser.
 *
 * Nothing replaces it. This client does not ask when a plan last changed: it refetches when
 * the delivery layer signals, so there is no timestamp to compare and nothing to hold.
 */
export type Plan = {
  readonly id: string;
  readonly name: string;
  readonly state: string;
  readonly createdAt: string;
  /** True only while a live lease is held. Never a promise that a reply is coming. */
  readonly facilitatorBusy: boolean;
  /**
   * Whether this plan is a scripted demo rather than a family's own plan. Read
   * DEFENSIVELY, same posture as `facilitatorBusy` and `self`: absent, null, or not a
   * boolean all mean false. The failure that direction is a demo shown as ordinary; the
   * other direction is a family's own plan marked "Demo" — the one that must not happen.
   * See `PlanView.isDemo` (`@mor/api`'s `views.ts`) for what the value means server-side.
   */
  readonly isDemo: boolean;
};

export type Thread = {
  readonly id: string;
  readonly kind: string;
  readonly acceptingMessages: boolean;
  readonly createdAt: string;
  /**
   * Whether THIS READER has messages in this thread they have not seen.
   *
   * Self-facing only — it never says anything about another participant's read position,
   * which is the line `docs/ARCHITECTURE.md`'s "deliberately not built" section now draws
   * explicitly between this and a read receipt.
   *
   * Read DEFENSIVELY, same posture as `facilitatorBusy` and `self`: absent, null, or not a
   * boolean all mean "not unread". The failure that direction is a missed badge; the other
   * direction is a badge that never clears, which reads as a broken feature rather than a
   * quiet one.
   */
  readonly unread: boolean;
};

/**
 * The roster references a message may carry, all already validated against this plan's
 * roster by the server. Nothing else in `metadata` is read.
 */
export type MessageMetadata = {
  /** Which person a facilitator message spoke to. The room can see who was asked. */
  readonly addressed: string | null;
  /** Who a system message is about. "Seth joined" has a subject and no author. */
  readonly subject: string | null;
  readonly systemKind:
    'joined' | 'confirmation_resolved' | 'speaking_notice' | 'invited' | null;
  readonly outcome: 'ratified' | 'declined' | null;
  readonly actionType: string | null;
  readonly target: string | null;
  /**
   * Who a ratified confirmation did not write to (issue #155, D96) — empty for every
   * other message, including a decline. Read defensively like every other field here: a
   * missing or malformed value reads as nobody, not as a reason to refuse the message.
   * `MessageRow.tsx` turns this into the permanent line in the thread; it names nobody
   * from this array directly, only from the People list it already reads.
   */
  readonly notWrittenTo: readonly string[];
};

export type Message = {
  readonly id: string;
  readonly threadId: string;
  /** A decimal string. The client compares it and never computes one. */
  readonly seq: string;
  readonly type: 'human' | 'facilitator' | 'system';
  readonly authorId: string | null;
  readonly body: string;
  readonly metadata: MessageMetadata;
  readonly createdAt: string;
};

/**
 * A person in the plan.
 *
 * `role` is deliberately ABSENT. The server sends it — `VIEW_KEYS.person` has seven
 * members and this type has six — and the client drops it on the floor, because "users
 * do not think in terms of principal and interested party, and the interface does not
 * present them". Dropping it here rather than declining to render it in a component is
 * the difference between a rule and a habit: there is no value in this process to label
 * anybody with. `test/contract.test.ts` asserts `role` is not a key of a decoded person.
 *
 * `email` is the field this type gained for issue #138: the address a correction has to
 * reach, read live off this row rather than recalled from a request payload frozen at
 * staging time. It arrives real only when THIS READER currently holds a principal's
 * grant — `views.ts` returns it for every row when the reader is a principal and null
 * for every row otherwise, because a principal is the only person who ever approves
 * sending to one of these addresses and so the only reader who needs to check it first.
 * Present and null for everybody else, never absent, which is what lets
 * `PendingActionRow.tsx` and `PersonRow.tsx` read the same field without a branch for
 * who is asking.
 *
 * `self` is the field this type gained, and it is the only thing in this client that says
 * who is holding the browser. It arrives ON THE ROSTER, in the same response as the names a
 * message is attributed with, which is why nothing in this package stores it: "which of
 * these people am I" is answered by the snapshot being rendered, so there is no identity
 * variable here to disagree with the server, none to survive a change of plan — an account
 * holds a different roster entry in each one — and none to go stale while a plan is open.
 */
export type Person = {
  readonly id: string;
  readonly name: string;
  readonly joined: boolean;
  /**
   * Whether a principal has approved inviting this person. Read the same way `joined` is —
   * a field the server always sends, decoded with `bool` — because it is the other half of
   * the same fact: `joined` says an account is bound, `invited` says a principal decided to
   * write to them. See `PersonView.invited` (`@mor/api`'s `views.ts`) for where it comes
   * from and why it is returned to every reader.
   */
  readonly invited: boolean;
  /**
   * The address to show for a correction to reach, or null. Read DEFENSIVELY, the same
   * posture as the other nullable strings this client decodes (`Objective.state`,
   * `Decision.resolution`): a server that omits the field reads as "no address to show"
   * rather than refusing the whole person.
   */
  readonly email: string | null;
  /**
   * Whether this is the person reading. True for exactly one entry in a response.
   *
   * Read DEFENSIVELY: absent, null, or not a boolean all mean false. The failure that
   * direction is your own messages looking like everybody else's; the failure the other
   * direction is somebody else's words attributed to you, in a conversation about a
   * parent's death. Only one of those is survivable, so the default is "not you".
   */
  readonly self: boolean;
  /**
   * Whether this seat is played by a model rather than a person — a demo's character. What
   * `MessageRow.tsx` marks a line with, so a reader can tell which of the names answering
   * them is fictional without holding the seat list in their head.
   *
   * Read DEFENSIVELY, the same posture as `self`, and the default falls the same way for
   * the same reason: absent, null, or not a boolean all mean false. Wrong in this
   * direction is a character's line missing its mark in a conversation the reader was
   * told at the door was fictional; wrong in the other is a real family member's words
   * labelled as a machine's. Only one of those is survivable.
   */
  readonly character: boolean;
};

/**
 * The plan's objective: what this whole exercise is for, in words safe for everyone who
 * ever joins. Maintained by the facilitator; nothing here is editable (same rule as the
 * decisions list — corrections happen in the conversation).
 */
export type Objective = {
  readonly id: string;
  readonly state: string | null;
  readonly text: string | null;
  readonly updatedAt: string;
};

export type Decision = {
  readonly id: string;
  readonly label: string;
  readonly state: string | null;
  readonly resolution: string | null;
  readonly updatedAt: string;
};

/**
 * An open ask the facilitator has put to the people who can answer it.
 *
 * D70: everybody in the conversation sees that an ask is open. Since every ask the
 * facilitator can raise is refused unless it is raised in a principals-only conversation,
 * and the server serves this list filtered to conversations the reader is in, everybody who
 * sees one can answer it. There is no second kind of row here and no bit distinguishing
 * them.
 *
 * `createdAt` is still not rendered anywhere as an age — see `plan/inline.ts`, which places
 * an ask by the conversation rather than by a clock.
 */
export type PendingAction = {
  /** The identifier `resolve` takes. The client only ever hands it back. */
  readonly id: string;
  readonly threadId: string;
  readonly type: string;
  readonly payload: InvitationPayload | MoveToDecidingPayload | null;
  readonly createdAt: string | null;
};

/**
 * The person-reference payload used by invitation and principal-grant confirmations.
 *
 * It carries no address. Issue #138: a request used to freeze one at staging time, and
 * a correction made afterward could never reach it. Nothing here names an address any
 * more — `PendingActionRow.tsx` looks the address up on the matching `Person` instead,
 * the same way it already looks up the name, so a correction reaches a standing ask with
 * no further machinery. An action of any other type arrives with an empty payload and
 * is rendered as an ask this build cannot show — never with a control built out of
 * fields nobody pinned.
 */
export type InvitationPayload = {
  readonly rosterEntryId: string | null;
};

/**
 * The other payload shape this client understands: the move to deciding, whose ask names
 * everyone being invited as the plan moves. Each entry names a roster reference exactly
 * as an invitation payload does; the address for each invitee is the same person lookup.
 */
export type MoveToDecidingPayload = {
  readonly invitees: readonly InvitationPayload[];
};

export const INVITATION_ACTION = 'send_invitation';
export const INVITATION_RATIFIED = 'ratified';
export const INVITATION_DECLINED = 'declined';
export const MOVE_ACTION = 'move_to_deciding';
export const GRANT_PRINCIPAL_ACTION = 'grant_principal';

/**
 * One entry in `release-notes.json` — not a `/v1` response but a static file this same
 * package ships, so it gets a decoder for the reason every response does: this client
 * believes only what it names one field at a time. `id` is the release pull request's
 * number, stable across regeneration and never shown; a person reads `version` (or, on an
 * older entry, `date`), `title`, and `notes`. See `WhatsNew.tsx`.
 *
 * `version` and `date` are each nullable, and exactly one is ever present. Every entry the
 * release-notes agent writes from now on carries `version`, the repository's build number
 * at the moment the entry was written. The five entries that shipped before that scheme
 * existed still carry `date`, unchanged, and stay that way rather than being backfilled.
 */
export type ReleaseNote = {
  readonly id: string;
  readonly version: number | null;
  readonly date: string | null;
  readonly title: string;
  readonly notes: readonly string[];
};

// ----------------------------------------------------------------------- the decoders

export function decodePlan(value: unknown): Plan {
  const raw = object(value);
  return Object.freeze({
    id: str(raw, 'id'),
    name: str(raw, 'name'),
    state: str(raw, 'state'),
    createdAt: str(raw, 'createdAt'),
    // No `updatedAt`. A server that still sent one — an older deployment, or somebody who
    // put it back — has it dropped here, because this decoder names its fields one at a
    // time and never spreads. See the note on `Plan`.
    facilitatorBusy: optionalBool(raw, 'facilitatorBusy'),
    isDemo: optionalBool(raw, 'isDemo'),
  });
}

export function decodeThread(value: unknown): Thread {
  const raw = object(value);
  return Object.freeze({
    id: str(raw, 'id'),
    kind: str(raw, 'kind'),
    acceptingMessages: bool(raw, 'acceptingMessages'),
    createdAt: str(raw, 'createdAt'),
    unread: optionalBool(raw, 'unread'),
  });
}

const MESSAGE_TYPES: ReadonlySet<string> = new Set(['human', 'facilitator', 'system']);

export function decodeMessage(value: unknown): Message {
  const raw = object(value);
  const type = str(raw, 'type');
  if (!MESSAGE_TYPES.has(type)) {
    // An unknown speaker is not rendered as a participant, and it is not rendered as the
    // facilitator either. Refusing the whole message is the only answer that cannot be
    // wrong about who said something.
    throw new MalformedResponse(`unknown message type ${type}`);
  }
  const metadata = raw['metadata'];
  return Object.freeze({
    id: str(raw, 'id'),
    threadId: str(raw, 'threadId'),
    seq: str(raw, 'seq'),
    type: type as Message['type'],
    authorId: nullableStr(raw, 'authorId'),
    body: str(raw, 'body'),
    metadata: decodeMetadata(metadata),
    createdAt: str(raw, 'createdAt'),
  });
}

function decodeMetadata(value: unknown): MessageMetadata {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return Object.freeze({
      addressed: null,
      subject: null,
      systemKind: null,
      outcome: null,
      actionType: null,
      target: null,
      notWrittenTo: Object.freeze([]),
    });
  }
  const raw = value as Raw;
  return Object.freeze({
    addressed: nullableStr(raw, 'addressed'),
    subject: nullableStr(raw, 'subject'),
    systemKind:
      raw['systemKind'] === 'joined' ||
      raw['systemKind'] === 'confirmation_resolved' ||
      raw['systemKind'] === 'speaking_notice' ||
      raw['systemKind'] === 'invited'
        ? raw['systemKind']
        : null,
    outcome:
      raw['outcome'] === 'ratified' || raw['outcome'] === 'declined'
        ? raw['outcome']
        : null,
    actionType: nullableStr(raw, 'actionType'),
    target: nullableStr(raw, 'target'),
    notWrittenTo: decodeRosterIdList(raw['notWrittenTo']),
  });
}

export function decodePerson(value: unknown): Person {
  const raw = object(value);
  return Object.freeze({
    id: str(raw, 'id'),
    name: str(raw, 'name'),
    joined: bool(raw, 'joined'),
    invited: bool(raw, 'invited'),
    email: nullableStr(raw, 'email'),
    self: optionalBool(raw, 'self'),
    character: optionalBool(raw, 'character'),
  });
}

export function decodeObjective(value: unknown): Objective {
  const raw = object(value);
  return Object.freeze({
    id: str(raw, 'id'),
    state: nullableStr(raw, 'state'),
    text: nullableStr(raw, 'text'),
    updatedAt: str(raw, 'updatedAt'),
  });
}

export function decodeObjectiveList(value: unknown): Objective[] {
  return array(object(value)['objectives']).map(decodeObjective);
}

export function decodeDecision(value: unknown): Decision {
  const raw = object(value);
  return Object.freeze({
    id: str(raw, 'id'),
    label: str(raw, 'label'),
    state: nullableStr(raw, 'state'),
    resolution: nullableStr(raw, 'resolution'),
    updatedAt: str(raw, 'updatedAt'),
  });
}

export function decodePendingAction(value: unknown): PendingAction {
  const raw = object(value);
  const type = str(raw, 'type');
  return Object.freeze({
    id: str(raw, 'id'),
    threadId: str(raw, 'threadId'),
    type,
    payload:
      type === INVITATION_ACTION || type === GRANT_PRINCIPAL_ACTION
        ? decodeInvitationPayload(raw['payload'])
        : type === MOVE_ACTION
          ? decodeMovePayload(raw['payload'])
          : null,
    createdAt: nullableStr(raw, 'createdAt'),
  });
}

function decodeInvitationPayload(value: unknown): InvitationPayload | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const raw = value as Raw;
  return Object.freeze({
    rosterEntryId: nullableStr(raw, 'rosterEntryId'),
  });
}

function decodeMovePayload(value: unknown): MoveToDecidingPayload | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const invitees = (value as Raw)['invitees'];
  if (!Array.isArray(invitees)) {
    return Object.freeze({ invitees: Object.freeze([]) });
  }
  return Object.freeze({
    invitees: Object.freeze(
      invitees
        .map((item) => decodeInvitationPayload(item))
        .filter((item): item is InvitationPayload => item !== null),
    ),
  });
}

// ------------------------------------------------------------------ envelope decoders

export function decodePlanEnvelope(value: unknown): Plan {
  return decodePlan(object(value)['plan']);
}

export function decodePlanList(value: unknown): Plan[] {
  return array(object(value)['plans']).map(decodePlan);
}

export function decodeThreadList(value: unknown): Thread[] {
  return array(object(value)['threads']).map(decodeThread);
}

export function decodePersonList(value: unknown): Person[] {
  return array(object(value)['people']).map(decodePerson);
}

export function decodeDecisionList(value: unknown): Decision[] {
  return array(object(value)['decisions']).map(decodeDecision);
}

export function decodePendingActionList(value: unknown): PendingAction[] {
  return array(object(value)['pendingActions']).map(decodePendingAction);
}

/**
 * What resolving a confirmation returns. Who this ratification did not write to no
 * longer rides on this response alone (issue #155, D96): it is on the confirmation's
 * own system message now, which is what survives once this response is gone —
 * `decodeMetadata`'s `notWrittenTo` is that same field, read off the message instead.
 */
export function decodePendingActionEnvelope(value: unknown): PendingAction {
  return decodePendingAction(object(value)['pendingAction']);
}

/**
 * A list of roster references, read defensively like `facilitatorBusy` and `self`: a
 * missing or malformed value reads as an empty list, not as a reason to refuse whatever
 * it was on. Shared by `decodeMetadata`'s `notWrittenTo`.
 */
function decodeRosterIdList(value: unknown): readonly string[] {
  if (!Array.isArray(value)) {
    return Object.freeze([]);
  }
  return Object.freeze(value.filter((item): item is string => typeof item === 'string'));
}

export type MessagePage = {
  readonly messages: readonly Message[];
  /** The server's cursor for the next older page. The client never builds one. */
  readonly nextBefore: string | null;
};

export function decodeMessagePage(value: unknown): MessagePage {
  const raw = object(value);
  return Object.freeze({
    messages: Object.freeze(array(raw['messages']).map(decodeMessage)),
    nextBefore: nullableStr(raw, 'nextBefore'),
  });
}

export function decodeMessageEnvelope(value: unknown): Message {
  return decodeMessage(object(value)['message']);
}

export function decodeRedemption(value: unknown): string {
  return str(object(object(value)['redemption']), 'planId');
}

/**
 * What `POST /v1/invitations/address` returns: the address an invitation was mailed to,
 * for `Invite.tsx` to hand the vendor's sign-up as a starting value before anyone has
 * signed in, and whether the plan it names is a demo, so that same screen can say so
 * before the person has any session to ask a plan through. This caller already holds an
 * invitation token — a live credential to the seat itself — so `isDemo` tells them nothing
 * the mail that carried the same token did not already say. See `InvitationAddressView`
 * (`@mor/api`'s `views.ts`) for the fuller reasoning.
 */
export type InvitationAddress = {
  readonly email: string;
  readonly isDemo: boolean;
};

/**
 * Same envelope shape as everything else in this module: named one field at a time off
 * `{ invitation: { email, isDemo } }`, nothing spread, nothing else read off `invitation`
 * even though the server sends nothing else there today. `isDemo` is read DEFENSIVELY,
 * the same posture as `Plan.isDemo`: absent, null, or not a boolean all mean false.
 */
export function decodeInvitationAddress(value: unknown): InvitationAddress {
  const raw = object(object(value)['invitation']);
  return Object.freeze({
    email: str(raw, 'email'),
    isDemo: optionalBool(raw, 'isDemo'),
  });
}

/**
 * The plan id out of a `plan.changed` frame, and nothing else.
 *
 * `at` is in the frame and is not read: the client has no use for when the server noticed,
 * and a signal handler that reads one field it does not need is a signal handler somebody
 * later reads a second field from. Everything about a plan comes from a fetch.
 */
export function decodeSignalPlanId(value: unknown): string {
  return str(object(value), 'planId');
}

/**
 * `release-notes.json` is a plain array, newest entry first, not an object with a key —
 * unlike every list above, which mirrors a `/v1` envelope. So this reads `value` directly
 * rather than pulling a field off it.
 */
export function decodeReleaseNoteList(value: unknown): ReleaseNote[] {
  return array(value).map(decodeReleaseNote);
}

export function decodeReleaseNote(value: unknown): ReleaseNote {
  const raw = object(value);
  const version = nullableNum(raw, 'version');
  const date = nullableStr(raw, 'date');
  if ((version === null) === (date === null)) {
    throw new MalformedResponse('expected exactly one of version or date');
  }
  return Object.freeze({
    id: str(raw, 'id'),
    version,
    date,
    title: str(raw, 'title'),
    notes: Object.freeze(
      array(raw['notes']).map((item) => {
        if (typeof item !== 'string') {
          throw new MalformedResponse('expected a note to be a string');
        }
        return item;
      }),
    ),
  });
}

// ------------------------------------------------------------------------ demo mode

/**
 * One seat in a demo scenario: a person's name, and whether a visitor may take it. See
 * `@mor/api`'s `demo/scenarios.ts` for the `SavedScenario` field this reduces —
 * `ScenarioPerson.playable`.
 */
export type DemoSeat = {
  readonly label: string;
  readonly name: string;
  readonly playable: boolean;
  /** What the picker shows in place of `name` — authored copy, shown only here, and empty
   *  when the scenario's author has not written one. See `@mor/eval`'s `scenario.ts` for
   *  `ScenarioPerson.introduction`, the field this carries. */
  readonly introduction: string;
  /** This seat, named so it reads with nothing else around it — authored copy, used
   *  wherever `{{character}}` is substituted rather than on the picker, and empty when the
   *  scenario's author has not written one. See `@mor/eval`'s `scenario.ts` for
   *  `ScenarioPerson.standaloneIntroduction`, the field this carries. */
  readonly standaloneIntroduction: string;
};

/**
 * One scenario `GET /v1/demo/scenarios` offers: a sentence about the family, the moment
 * a person would arrive at, and every seat. `sentence` and `moment` are authored copy —
 * see `@mor/api`'s `demo/blurbs.ts` — never derived from anything a person typed inside
 * a plan.
 */
export type DemoScenario = {
  readonly id: string;
  readonly name: string;
  readonly sentence: string;
  readonly moment: string;
  /** The words shown once, the first time a person arrives in a plan started from this
   *  scenario — a list of paragraphs, authored copy, empty when the scenario's author has
   *  not written one. See `@mor/eval`'s `scenario.ts` for `SavedScenario.welcome`. */
  readonly welcome: readonly string[];
  readonly seats: readonly DemoSeat[];
};

function decodeDemoSeat(value: unknown): DemoSeat {
  const raw = object(value);
  return Object.freeze({
    label: str(raw, 'label'),
    name: str(raw, 'name'),
    playable: bool(raw, 'playable'),
    introduction: str(raw, 'introduction'),
    standaloneIntroduction: str(raw, 'standaloneIntroduction'),
  });
}

function decodeDemoScenario(value: unknown): DemoScenario {
  const raw = object(value);
  return Object.freeze({
    id: str(raw, 'id'),
    name: str(raw, 'name'),
    sentence: str(raw, 'sentence'),
    moment: str(raw, 'moment'),
    welcome: Object.freeze(
      array(raw['welcome']).map((item) => {
        if (typeof item !== 'string') {
          throw new MalformedResponse('expected a demo welcome line to be a string');
        }
        return item;
      }),
    ),
    seats: Object.freeze(array(raw['seats']).map(decodeDemoSeat)),
  });
}

export function decodeDemoScenarioList(value: unknown): DemoScenario[] {
  return array(object(value)['scenarios']).map(decodeDemoScenario);
}

/**
 * What `POST /v1/demo/scenarios/start` returns: the new plan's id, and the scenario and
 * seat it was started from.
 */
export type DemoStart = {
  readonly planId: string;
  readonly scenarioId: string;
  readonly seatLabel: string;
};

export function decodeDemoStart(value: unknown): DemoStart {
  const raw = object(object(value)['demo']);
  return Object.freeze({
    planId: str(raw, 'planId'),
    scenarioId: str(raw, 'scenarioId'),
    seatLabel: str(raw, 'seatLabel'),
  });
}

/**
 * What `GET /v1/plans/:planId/demo/source` returns: which scenario and seat a demo plan
 * was loaded from — read fresh from the server everywhere it is needed
 * (`PlanScreen.tsx`, `CharacterSheet.tsx`), which is what replaced the per-tab
 * `sessionStorage` `demo-stash.ts` used to hold this in. See `@mor/api`'s
 * `views.ts`'s `DemoSourceView` for why the hash and the starting account are not here.
 */
export type DemoSource = {
  readonly scenarioId: string;
  readonly seatLabel: string;
};

export function decodeDemoSource(value: unknown): DemoSource {
  const raw = object(object(value)['demo']);
  return Object.freeze({
    scenarioId: str(raw, 'scenarioId'),
    seatLabel: str(raw, 'seatLabel'),
  });
}

/** The markdown text of one character sheet, from
 *  `GET /v1/demo/scenarios/:id/sheets/:seat`. */
export function decodeDemoSheet(value: unknown): string {
  return str(object(object(value)['sheet']), 'text');
}
